import { z } from 'zod';

// CRA's DefinePlugin only inlines LITERAL `process.env.REACT_APP_*` references at
// build time. We declare a minimal `process` shape here so TS compiles in the
// browser, while keeping the literal access pattern CRA requires.
declare const process: { env: Record<string, string | undefined> };

type RuntimeEnv = Record<string, string | undefined>;

const DEFAULT_BACKEND_URL = 'https://api.commu.click';
const DEFAULT_WEB_URL = 'https://commu.click';
const DEFAULT_WEB_TITLE = 'Communicate POS';
const LOCAL_BACKEND_URL = 'http://localhost:5000';

const EnvSchema = z.object({
    VITE_BACKEND_LOCATION: z.string().url(),
    VITE_WEB_URL: z.string().url(),
    VITE_WEB_TITLE: z.string().min(1),
    FIREBASE_API_KEY: z.string().min(1).optional(),
    FIREBASE_AUTH_DOMAIN: z.string().min(1).optional(),
    FIREBASE_PROJECT_ID: z.string().min(1).optional(),
    FIREBASE_STORAGE_BUCKET: z.string().min(1).optional(),
    FIREBASE_MESSAGING_SENDER_ID: z.string().min(1).optional(),
    FIREBASE_APP_ID: z.string().min(1).optional(),
    FIREBASE_VAPID_KEY: z.string().min(1).optional(),
});

type Env = z.infer<typeof EnvSchema>;

// Summary: Reads Vite env safely without hard dependency on import.meta syntax.
// Details: docs/function-library/web/env-util.md#readmetaenv
const readMetaEnv = (): RuntimeEnv | undefined => {
    try {
        // eslint-disable-next-line no-new-func
        return Function(
            'return (typeof import.meta !== "undefined" && import.meta.env) ? import.meta.env : undefined;',
        )() as RuntimeEnv | undefined;
    } catch {
        return undefined;
    }
};

// Summary: Reads CRA env using literal process.env keys so DefinePlugin can inline.
// Details: docs/function-library/web/env-util.md#readcraenv
const readCraEnv = (): RuntimeEnv => {
    // CRA requires literal `process.env.REACT_APP_*` reads for build-time replacement.
    try {
        return {
            VITE_BACKEND_LOCATION: process.env.REACT_APP_BACKEND_LOCATION,
            VITE_WEB_URL: process.env.REACT_APP_WEB_URL,
            VITE_WEB_TITLE: process.env.REACT_APP_WEB_TITLE,
            FIREBASE_API_KEY: process.env.REACT_APP_FIREBASE_API_KEY,
            FIREBASE_AUTH_DOMAIN: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
            FIREBASE_PROJECT_ID: process.env.REACT_APP_FIREBASE_PROJECT_ID,
            FIREBASE_STORAGE_BUCKET: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
            FIREBASE_MESSAGING_SENDER_ID: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
            FIREBASE_APP_ID: process.env.REACT_APP_FIREBASE_APP_ID,
            FIREBASE_VAPID_KEY: process.env.REACT_APP_FIREBASE_VAPID_KEY,
        };
    } catch {
        return {};
    }
};

// Summary: Detects local browser host for local-backend fallback behavior.
// Details: docs/function-library/web/env-util.md#islocalhosthost
const isLocalHostHost = (): boolean => {
    if (typeof window === 'undefined') {
        return false;
    }
    return window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
};

// Summary: Builds raw runtime env object using strict precedence and safe defaults.
// Details: docs/function-library/web/env-util.md#readbuildenv
const readBuildEnv = () => {
    const metaEnv = readMetaEnv();
    const craEnv = readCraEnv();
    const isLocalHost = isLocalHostHost();

    return {
        VITE_BACKEND_LOCATION:
            metaEnv?.VITE_BACKEND_LOCATION ??
            craEnv.VITE_BACKEND_LOCATION ??
            (isLocalHost ? LOCAL_BACKEND_URL : undefined) ??
            DEFAULT_BACKEND_URL,
        VITE_WEB_URL:
            metaEnv?.VITE_WEB_URL ??
            craEnv.VITE_WEB_URL ??
            DEFAULT_WEB_URL,
        VITE_WEB_TITLE:
            metaEnv?.VITE_WEB_TITLE ??
            craEnv.VITE_WEB_TITLE ??
            DEFAULT_WEB_TITLE,
        FIREBASE_API_KEY: metaEnv?.VITE_FIREBASE_API_KEY ?? craEnv.FIREBASE_API_KEY,
        FIREBASE_AUTH_DOMAIN: metaEnv?.VITE_FIREBASE_AUTH_DOMAIN ?? craEnv.FIREBASE_AUTH_DOMAIN,
        FIREBASE_PROJECT_ID: metaEnv?.VITE_FIREBASE_PROJECT_ID ?? craEnv.FIREBASE_PROJECT_ID,
        FIREBASE_STORAGE_BUCKET: metaEnv?.VITE_FIREBASE_STORAGE_BUCKET ?? craEnv.FIREBASE_STORAGE_BUCKET,
        FIREBASE_MESSAGING_SENDER_ID: metaEnv?.VITE_FIREBASE_MESSAGING_SENDER_ID ?? craEnv.FIREBASE_MESSAGING_SENDER_ID,
        FIREBASE_APP_ID: metaEnv?.VITE_FIREBASE_APP_ID ?? craEnv.FIREBASE_APP_ID,
        FIREBASE_VAPID_KEY: metaEnv?.VITE_FIREBASE_VAPID_KEY ?? craEnv.FIREBASE_VAPID_KEY,
    };
};

// Summary: Validates and returns runtime env config consumed by services and app boot.
// Details: docs/function-library/web/env-util.md#validateenv
export const validateEnv = (): Env => {
    const parsedEnv = EnvSchema.safeParse(readBuildEnv());

    if (!parsedEnv.success) {
        const issues = parsedEnv.error.issues.map((issue) => `${issue.path.join('.')}: ${issue.message}`).join('; ');
        console.error(parsedEnv.error);
        throw new Error(`Invalid environment variables: ${issues}`);
    }

    return parsedEnv.data;
};

export type FirebaseWebConfig = {
    apiKey: string;
    authDomain: string;
    projectId: string;
    storageBucket: string;
    messagingSenderId: string;
    appId: string;
    vapidKey: string;
};

export const getFirebaseWebConfig = (): FirebaseWebConfig | null => {
    const env = validateEnv();
    if (
        !env.FIREBASE_API_KEY ||
        !env.FIREBASE_AUTH_DOMAIN ||
        !env.FIREBASE_PROJECT_ID ||
        !env.FIREBASE_STORAGE_BUCKET ||
        !env.FIREBASE_MESSAGING_SENDER_ID ||
        !env.FIREBASE_APP_ID ||
        !env.FIREBASE_VAPID_KEY
    ) {
        return null;
    }

    return {
        apiKey: env.FIREBASE_API_KEY,
        authDomain: env.FIREBASE_AUTH_DOMAIN,
        projectId: env.FIREBASE_PROJECT_ID,
        storageBucket: env.FIREBASE_STORAGE_BUCKET,
        messagingSenderId: env.FIREBASE_MESSAGING_SENDER_ID,
        appId: env.FIREBASE_APP_ID,
        vapidKey: env.FIREBASE_VAPID_KEY,
    };
};
